
import React, { useState, useEffect } from 'react';
import { SavedResult, TypingMode } from '../types';
import { getGlobalLeaderboard } from '../services/dbService';
import { Trophy, ChevronLeft, Calendar, User, Target, Hash, Clock, Globe } from 'lucide-react';

interface Props {
  onClose: () => void;
}

export const LeaderboardView: React.FC<Props> = ({ onClose }) => {
  const [results, setResults] = useState<SavedResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeConfig, setActiveConfig] = useState(30);

  useEffect(() => {
    const fetchBoard = async () => {
      setLoading(true);
      const data = await getGlobalLeaderboard(TypingMode.TIME, activeConfig);
      setResults(data);
      setLoading(false);
    };
    fetchBoard();
  }, [activeConfig]);

  const maskEmail = (email?: string) => {
    if (!email) return 'Anonymous';
    const [name, domain] = email.split('@');
    if (name.length <= 3) return email;
    return `${name.substring(0, 3)}***@${domain}`;
  };

  const formatDate = (ts: number) => {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date(ts));
  };

  return (
    <div className="w-full max-w-5xl animate-fadeIn pb-20">
      <div className="flex justify-between items-center mb-10">
        <button 
          onClick={onClose}
          className="flex items-center space-x-2 text-sub hover:text-text transition-colors"
        >
          <ChevronLeft size={20} />
          <span>Back</span>
        </button>
        <h2 className="text-2xl font-bold flex items-center space-x-3">
          <Trophy size={24} className="text-main" />
          <span>Leaderboards</span>
        </h2>
        <div className="w-20"></div> {/* Spacer */}
      </div>

      <div className="flex justify-center mb-8 bg-sub/5 p-1 rounded-xl w-fit mx-auto">
        {[15, 30, 60].map(val => (
          <button 
            key={val}
            onClick={() => setActiveConfig(val)}
            className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${activeConfig === val ? 'text-main bg-sub/10' : 'text-sub hover:text-text'}`}
          >
            {val}s
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-main"></div>
          <span className="text-sub text-xs uppercase tracking-widest">Fetching rankings...</span>
        </div>
      ) : results.length === 0 ? (
        <div className="bg-sub/5 rounded-2xl p-20 text-center border border-sub/10">
          <Target size={48} className="mx-auto mb-4 opacity-10" />
          <p className="text-sub">No rankings for this category yet.</p>
          <p className="text-[10px] text-sub/50 mt-2 italic">Note: Indices might be building in Firestore.</p>
        </div>
      ) : (
        <div className="bg-sub/5 rounded-2xl overflow-hidden border border-sub/10 shadow-2xl">
          <table className="w-full text-left">
            <thead>
              <tr className="text-sub uppercase text-[10px] font-bold tracking-widest border-b border-sub/10">
                <th className="px-6 py-5 w-16">#</th>
                <th className="px-6 py-5">User</th>
                <th className="px-6 py-5">WPM</th>
                <th className="px-6 py-5">Accuracy</th>
                <th className="px-6 py-5">Language</th>
                <th className="px-6 py-5 text-right">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-sub/5">
              {results.map((r, idx) => (
                <tr key={r.id} className="hover:bg-sub/5 transition-colors group">
                  <td className="px-6 py-5">
                    {idx === 0 ? <Trophy size={18} className="text-yellow-500" /> : 
                     idx === 1 ? <Trophy size={18} className="text-slate-300" /> : 
                     idx === 2 ? <Trophy size={18} className="text-amber-600" /> : 
                     <span className="text-sub font-mono">{idx + 1}</span>}
                  </td>
                  <td className="px-6 py-5">
                    <div className="flex items-center space-x-2">
                        <User size={14} className="text-sub opacity-50" />
                        <span className="text-sm font-medium">{maskEmail(r.userEmail)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-5">
                    <span className="text-main font-bold text-lg">{r.stats.wpm}</span>
                  </td>
                  <td className="px-6 py-5 text-sub">{r.stats.accuracy}%</td>
                  <td className="px-6 py-5 text-sub uppercase text-xs">{r.language.substring(0, 2)}</td>
                  <td className="px-6 py-5 text-right text-sub text-xs">
                    <div className="flex items-center justify-end space-x-1 opacity-50">
                        <Calendar size={12} />
                        <span>{formatDate(r.timestamp)}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-8 text-center text-[10px] text-sub uppercase tracking-widest opacity-30">
        Top 50 global results
      </div>
    </div>
  );
};
